import { useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Button, Stack, Typography } from "@mui/material";
import {
    DataGrid,
    getGridDateOperators,
    getGridSingleSelectOperators,
    getGridStringOperators,
    GridColDef,
    GridColumnVisibilityModel,
    GridFilterModel,
    GridRenderCellParams,
    GridRowParams,
    GridSortModel,
    ValueOptions,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";

import { DefaultPageLayout } from "features/core/DefaultPageLayout";
import { GlobalErrorMessage } from "features/core/GlobalErrorMessage";
import { useSignalRGroupSubscriber } from "features/messages/hooks/useSignalRGroup";
import { dossierGroupId } from "features/messages/hubs/sections/DossierSection";

import { Dossier } from "types/Dossier";
import { PaginatedRequest } from "types/PaginatedRequest";
import { PersistedRecordingDownloadStatus } from "types/PersistedRecordingDownloadStatus";

import { CreateDossierDialog } from "./CreateDossierDialog";
import { StatusTotalsBarChart } from "../common/StatusTotalsBarChart";
import { DossierCapabilityColumn } from "./DossierCapabilityColumn";
import { DossierActionsPopOver } from "./DossierActionsPopOver";
import { useGetDossiersQuery } from "services/streamingApi/caseManager.api";
import { dossierOverviewSelectors } from "./store/selectors.dossierOverview";
import { useAppSelector } from "app/hooks";
import { DossierPriority } from "types/DossierPriority";
import { formatEnumValueWithSpaces } from "utils/formatEnumValueWithSpaces";
import { DossierExportsColumn } from "./DossierExportsColumn";
import { AppFeature } from "features/appFeatures/types/AppFeature";
import { appFeaturesSelectors } from "features/appFeatures/store/selectors";
import { DateTime } from "luxon";
import { DossierStatus } from "types/DossierStatus";

export interface DossiersOverviewProps {
    drawerWidth: number;
}

const persistedRecordingDownloadStatusSeries = [
    {
        status: PersistedRecordingDownloadStatus.Completed,
        label: "Completed",
        color: "#10B981",
    },
    {
        status: PersistedRecordingDownloadStatus.Scheduled,
        label: "Scheduled",
        color: "#3B82F6",
    },
    {
        status: PersistedRecordingDownloadStatus.Downloading,
        label: "Downloading",
        color: "#8B5CF6",
    },
    {
        status: PersistedRecordingDownloadStatus.Converting,
        label: "Converting",
        color: "#06B6D4",
    },
    {
        status: PersistedRecordingDownloadStatus.Uploading,
        label: "Uploading",
        color: "#0EA5E9",
    },
    {
        status: PersistedRecordingDownloadStatus.Queued,
        label: "Queued",
        color: "#6B7280",
    },
    {
        status: PersistedRecordingDownloadStatus.QueuedForRetryByUser,
        label: "Queued for retry by user",
        color: "#F59E0B",
    },
    {
        status: PersistedRecordingDownloadStatus.Failed,
        label: "Failed",
        color: "#EF4444",
    },
];

const supportedStringFilterOperators = getGridStringOperators(false).filter(
    (operator) =>
        operator.value !== "doesNotContain" &&
        operator.value !== "isEmpty" &&
        operator.value !== "isNotEmpty" &&
        operator.value !== "isAnyOf",
);

const supportedDateFilterOperators = getGridDateOperators(false).filter(
    (operator) =>
        operator.value !== "is" &&
        operator.value !== "isNot" &&
        operator.value !== "isEmpty" &&
        operator.value !== "isNotEmpty",
);

const supportedSingleSelectFilterOperators =
    getGridSingleSelectOperators().filter(
        (operator) => operator.value !== "isAnyOf",
    );

const columns: GridColDef<Dossier>[] = [
    {
        field: "name",
        headerName: "Name",
        minWidth: 200,
        flex: 1,
        filterOperators: supportedStringFilterOperators,
    },
    {
        field: "siteId",
        headerName: "Site",
        width: 100,
        filterOperators: supportedStringFilterOperators,
    },
    {
        field: "dossierDownloadStatusStatistics",
        headerName: "Status",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableExport: true,
        width: 150,
        renderCell: (param: GridRenderCellParams<Dossier>) => {
            if (!param.value) {
                return null;
            }

            return (
                <StatusTotalsBarChart
                    dossierDownloadStatusStatistics={param.value}
                    series={persistedRecordingDownloadStatusSeries}
                    completedStatus={PersistedRecordingDownloadStatus.Completed}
                    title={"Persisted Recordings"}
                />
            );
        },
    },
    {
        field: "created",
        headerName: "Created",
        minWidth: 150,
        type: "dateTime",
        valueFormatter: (value) =>
            value && DateTime.fromISO(value).toRelative(),
        filterOperators: supportedDateFilterOperators,
    },
    {
        field: "createdBy",
        headerName: "Created By",
        minWidth: 175,
        filterOperators: supportedStringFilterOperators,
    },
    {
        field: "startTime",
        headerName: "Start Time",
        minWidth: 175,
        type: "dateTime",
        valueGetter: (value) => (value ? new Date(value) : undefined),
        filterOperators: supportedDateFilterOperators,
    },
    {
        field: "endTime",
        headerName: "End Time",
        minWidth: 175,
        type: "dateTime",
        valueGetter: (value) => (value ? new Date(value) : undefined),
        filterOperators: supportedDateFilterOperators,
    },
    {
        field: "priority",
        headerName: "Priority",
        minWidth: 125,
        type: "singleSelect",
        valueOptions: Object.values(DossierPriority).map<ValueOptions>(
            (rawValue) => ({
                value: rawValue,
                label: formatEnumValueWithSpaces<DossierPriority>(rawValue),
            }),
        ),
        filterOperators: supportedSingleSelectFilterOperators,
        renderCell: (param: GridRenderCellParams<Dossier>) => {
            return formatEnumValueWithSpaces<DossierPriority>(param.value);
        },
    },
    {
        field: "externalReference",
        headerName: "Reference",
        minWidth: 100,
        filterOperators: supportedStringFilterOperators,
    },
    {
        field: "dossierCapabilities",
        headerName: "Capabilities",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (param: GridRenderCellParams<Dossier>) => {
            return (
                <DossierCapabilityColumn
                    dossierCapabilityNames={param.row.dossierCapabilityNames}
                />
            );
        },
    },
    {
        field: "exportsAvailable",
        headerName: "Exports",
        width: 75,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        renderCell: (param: GridRenderCellParams<Dossier>) => {
            return <DossierExportsColumn dossier={param.row} />;
        },
    },
    {
        field: "id",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (param: GridRenderCellParams<Dossier>) => {
            return <DossierActionsPopOver dossier={param.row} />;
        },
    },
];

export function DossiersOverview(props: DossiersOverviewProps) {
    const navigate = useNavigate();

    // subscribe to SignalR updates of dossiers
    useSignalRGroupSubscriber(dossierGroupId);

    // store
    const dossiers = useAppSelector(dossierOverviewSelectors.selectAllDossiers);
    const totalDossiers = useAppSelector(
        dossierOverviewSelectors.selectTotalDossiers,
    );
    const isLoadingDossiers = useAppSelector(
        dossierOverviewSelectors.selectIsLoadingDossiers,
    );
    const errorLoadingDossiers = useAppSelector(
        dossierOverviewSelectors.selectErrorLoadingDossiers,
    );
    const userHasDossierExportFeature = useAppSelector((state) =>
        appFeaturesSelectors.selectUserHasFeatures(state, [
            AppFeature.CaseManagementExports,
        ]),
    );

    // state and handlers
    const [showCreateDossier, setShowCreateDossier] = useState(false);
    const [gridSortModel, setGridSortModel] = useState<GridSortModel>([
        { field: "created", sort: "desc" },
    ]); // default sort
    const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
        items: [],
    });

    const [paginatedRequest, setPaginatedRequest] = useState<PaginatedRequest>({
        page: 0,
        pageSize: 25,
    });

    const orderByString = useMemo(() => {
        if (!gridSortModel) {
            return;
        }

        return gridSortModel.map((x) => `${x.field} ${x.sort}`).join(",") ?? "";
    }, [gridSortModel]);

    const filterByString = useMemo(() => {
        if (!gridFilterModel) {
            return;
        }

        // Correct date format for API usage
        gridFilterModel.items.forEach((x) => {
            if (x.value && x.value instanceof Date) {
                x.value = x.value.toISOString();
            }
        });

        return (
            gridFilterModel.items
                .filter((x) => x.value !== undefined)
                .map((x) => `${x.field} ${x.operator} '${x.value}'`)
                .join(",") ?? ""
        );
    }, [gridFilterModel]);

    const searchByString = useMemo(() => {
        if (!gridFilterModel?.quickFilterValues) {
            return;
        }

        return gridFilterModel.quickFilterValues.join(" ");
    }, [gridFilterModel]);

    useGetDossiersQuery(
        {
            ...paginatedRequest,
            page: paginatedRequest.page + 1,
            orderBy: orderByString,
            filterBy: filterByString,
            searchBy: searchByString,
        },
        {
            refetchOnMountOrArgChange: true,
        },
    );

    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>({
            exportsAvailable: userHasDossierExportFeature,
        });

    return (
        <>
            <DefaultPageLayout
                drawerWidth={props.drawerWidth}
                main={
                    <Stack spacing={3}>
                        <Typography variant="h4">Dossiers</Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mb: 1 }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setShowCreateDossier(true)}
                            >
                                Create dossier
                            </Button>
                        </Stack>

                        <DataGrid
                            density={"standard"}
                            rows={dossiers}
                            rowCount={totalDossiers ?? -1}
                            getRowClassName={(params) =>
                                params.row.status === DossierStatus.Archived
                                    ? "row-archived"
                                    : ""
                            }
                            sx={{
                                "& .row-archived": {
                                    opacity: 0.55, // overall fade
                                },
                            }}
                            estimatedRowCount={500}
                            columns={columns}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={(newModel) =>
                                setColumnVisibilityModel(newModel)
                            }
                            filterMode={"server"}
                            filterDebounceMs={800}
                            onFilterModelChange={setGridFilterModel}
                            filterModel={gridFilterModel}
                            paginationMode={"server"}
                            paginationModel={paginatedRequest}
                            onPaginationModelChange={setPaginatedRequest}
                            sortingMode={"server"}
                            onSortModelChange={setGridSortModel}
                            sortModel={gridSortModel}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: paginatedRequest.pageSize,
                                    },
                                },
                            }}
                            onRowClick={(params: GridRowParams<Dossier>) => {
                                if (
                                    params.row.status === DossierStatus.Archived
                                ) {
                                    return;
                                }
                                navigate("/cases/" + params.row.id);
                            }}
                            showToolbar
                            loading={isLoadingDossiers}
                            slotProps={{
                                loadingOverlay: {
                                    variant: "linear-progress",
                                    noRowsVariant: "skeleton",
                                },
                                toolbar: {
                                    showQuickFilter: true,
                                },
                            }}
                            localeText={{
                                noResultsOverlayLabel: "No dossiers found",
                            }}
                        />

                        {errorLoadingDossiers && (
                            <GlobalErrorMessage
                                title={"Unable to fetch dossiers"}
                                message={"Please contact the service desk."}
                                debugData={{ errorLoadingDossiers }}
                                alertSeverity={"error"}
                            />
                        )}
                    </Stack>
                }
            />
            {showCreateDossier && (
                <CreateDossierDialog
                    closeDialog={() => setShowCreateDossier(false)}
                />
            )}
        </>
    );
}
